// -----------------------------------------------------------------------------
//  THE PHONE'S OWN DATABASE
// -----------------------------------------------------------------------------
//
//  Three separate things have to survive with no signal, and they are usually
//  confused with each other:
//
//    1. THE APP ITSELF must load. That is the service worker's job, not this
//       file's — a phone with no signal and no cached app shows a dinosaur.
//
//    2. WHAT THE OFFICER NEEDS TO READ must already be here: the village, the
//       code lists, and the households already registered. Otherwise the list
//       screen is empty in the field and the officer cannot tell whether a
//       house was recorded yesterday.
//
//    3. WHAT THE OFFICER WRITES must be kept until the server accepts it.
//       That is `outbox`, and it is the one table in this file whose loss
//       would be unrecoverable.
//
//  A NOTE ON CHANGING THE SCHEMA LATER
//  -----------------------------------
//  Dexie versions are how IndexedDB is migrated. A version bump that drops or
//  renames `outbox` DISCARDS QUEUED FIELDWORK on every phone that upgrades —
//  silently, on next launch, with no error and no way to get it back. If the
//  outbox shape ever has to change, add a version that MAPS the old rows into
//  the new shape, and never one that starts fresh. Everything else in here is
//  a cache and may be thrown away freely.
// -----------------------------------------------------------------------------

import Dexie, { type Table } from 'dexie'
import type { OutboxEntry, OutboxStore } from './outbox'

/** A household as the phone holds it, whether or not the server has seen it. */
export type LocalHousehold = {
  household_uid: string
  village_id: string
  household_code: string | null
  head_name: string | null
  head_sex: string | null
  household_size: number
  adults_male_18_59: number
  adults_female_18_59: number
  children_under_18: number
  elderly_60_plus: number
  members_with_disability: number
  record_status: string
  has_latrine: boolean | null
  latrine_type: string | null
  water_source: string | null
  /**
   * Plain numbers, not the geography blob PostgREST would otherwise send.
   * Migration 012 added them to v_households precisely so the phone can
   * measure distance for the duplicate check with no signal.
   */
  lat: number | null
  lon: number | null
  gps_accuracy_m: number | null
  gps_captured_at: string | null
  last_assessed_at: string | null
  updated_at: string
  /**
   * True while this household is still only on the phone. The list screen
   * shows it differently, because "saved" and "the office can see it" are
   * different facts and an officer is entitled to know which one is true.
   */
  pending: boolean
}

export type LocalVillage = {
  village_id: string
  code: string
  name_en: string
  name_km: string | null
  estimated_households: number | null
}

export type LocalRefOption = {
  id: string // list_code + '/' + code — IndexedDB wants a single primary key
  list_code: string
  code: string
  label_en: string
  label_km: string | null
}

export type MetaRow = { key: string; value: unknown }

class VillageMappingDB extends Dexie {
  outbox!: Table<OutboxEntry, number>
  households!: Table<LocalHousehold, string>
  villages!: Table<LocalVillage, string>
  ref_options!: Table<LocalRefOption, string>
  meta!: Table<MetaRow, string>

  constructor() {
    super('village-mapping')
    this.version(1).stores({
      // ++seq gives queue order for free; &op_id refuses the same entry twice
      // even if a screen manages to queue it on a double tap.
      outbox: '++seq, &op_id, status, household_uid, created_at',
      // `pending` is deliberately NOT indexed: IndexedDB cannot index a
      // boolean, and a "working" index over booleans silently matches nothing.
      // It is filtered in memory instead — a village holds a few hundred rows.
      households: '&household_uid, village_id, updated_at',
      villages: '&village_id, code',
      ref_options: '&id, list_code',
      meta: '&key',
    })
  }
}

export const db = new VillageMappingDB()

// -----------------------------------------------------------------------------
//  The outbox, wearing the interface the sync engine expects.
//  Keeping this adapter thin is what lets outbox.ts be tested without a browser.
// -----------------------------------------------------------------------------

export const dexieOutbox: OutboxStore = {
  async list() {
    return db.outbox.orderBy('seq').toArray()
  },
  async add(entry) {
    await db.outbox.add(entry as OutboxEntry)
  },
  async update(op_id, patch) {
    await db.outbox.where('op_id').equals(op_id).modify(patch)
  },
  async remove(op_id) {
    await db.outbox.where('op_id').equals(op_id).delete()
  },
}

/** How many pieces of work are waiting, and how many need a human. */
export async function outboxCounts(): Promise<{ pending: number; rejected: number }> {
  const [pending, rejected] = await Promise.all([
    db.outbox.where('status').equals('pending').count(),
    db.outbox.where('status').equals('rejected').count(),
  ])
  return { pending, rejected }
}

// -----------------------------------------------------------------------------
//  CACHING WHAT THE OFFICER WILL NEED
// -----------------------------------------------------------------------------
//  Called whenever there IS signal. The point is that the officer never has to
//  think about it: by the time they reach the village the phone already knows
//  everything it needs, because it topped itself up at the office over wifi.
// -----------------------------------------------------------------------------

export async function cacheVillages(rows: LocalVillage[]) {
  await db.villages.bulkPut(rows)
}

export async function cacheRefOptions(
  rows: Array<{ list_code: string; code: string; label_en: string; label_km: string | null }>,
) {
  await db.ref_options.bulkPut(
    rows.map((r) => ({ ...r, id: `${r.list_code}/${r.code}` })),
  )
}

/**
 * Replace the cached households of one village with what the server just sent.
 *
 * DELIBERATELY does not touch households still marked `pending`. A server
 * response cannot know about a house registered ten minutes ago in a village
 * with no signal, and deleting it because the server did not mention it would
 * destroy the very record the outbox is holding.
 */
export async function cacheHouseholds(villageId: string, rows: Omit<LocalHousehold, 'pending'>[]) {
  await db.transaction('rw', db.households, async () => {
    const localOnly = await db.households
      .where('village_id')
      .equals(villageId)
      .filter((h) => h.pending)
      .toArray()
    const keep = new Set(localOnly.map((h) => h.household_uid))

    await db.households
      .where('village_id')
      .equals(villageId)
      .filter((h) => !keep.has(h.household_uid))
      .delete()

    await db.households.bulkPut(
      rows.filter((r) => !keep.has(r.household_uid)).map((r) => ({ ...r, pending: false })),
    )
  })
}

/** Everything the phone knows about a village, cached or pending, newest first. */
export async function localHouseholds(villageId: string): Promise<LocalHousehold[]> {
  const rows = await db.households.where('village_id').equals(villageId).toArray()
  return rows.sort((a, b) => (a.updated_at < b.updated_at ? 1 : -1))
}

export async function localVillages(): Promise<LocalVillage[]> {
  return db.villages.orderBy('code').toArray()
}

export async function localRefOptions(lists: string[]): Promise<LocalRefOption[]> {
  return db.ref_options.where('list_code').anyOf(lists).toArray()
}

export async function setMeta(key: string, value: unknown) {
  await db.meta.put({ key, value })
}

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db.meta.get(key))?.value as T | undefined
}

/**
 * Clearing local data on sign-out — but NEVER the outbox.
 *
 * Signing out is not a reason to throw away work the server has not accepted.
 * If the officer signs out with 12 households queued, those 12 households are
 * still the only copy that exists. They wait for the next sign-in.
 */
export async function clearCachesKeepingOutbox() {
  await db.households.filter((h) => !h.pending).delete()
  await Promise.all([db.villages.clear(), db.ref_options.clear(), db.meta.clear()])
}
